import { Grid, useEventCallback } from '@mui/material';
import { Fragment, memo, useEffect, useMemo } from 'react';
import { FormikErrors, FormikTouched } from 'formik';
import { SectionVM } from 'civil-status/view-models/step-form';
import PmiTextField from 'shell/components/shared/text-field';
import { SelectChangeEvent } from '@mui/material';
import { Checkbox } from '@mui/material';
import { FormControlLabel } from '@mui/material';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import ListParentRecognition from '../list/list-parent-recognition';
import { DatePicker } from '@mui/lab';
import ListAnsc from '../../../../../../shared/list-ansc';
import { ItemVM } from 'shell/view-models/item';

interface GeneralDataBornProps {
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  onChanges: (values: SectionVM) => void;
  configuration?: string;
  controllIsPaternitySentence: (isPaternitySentence?: boolean) => void;
  controllParentRecognitionMadrePadreRiconoscimento: (parentRecognition?: string) => void;
  controllParentChangedSurname: (parentChangedSurname?: string) => void;
  controllMeasureExchangeSurname: (measureExchangeSurname?: string) => void;
}

const GeneralDataBornComponent = ({
  disabled = false,
  values,
  errors,
  touched,
  onChange,
  onChanges,
  configuration,
  controllIsPaternitySentence,
  controllParentRecognitionMadrePadreRiconoscimento,
  controllParentChangedSurname,
  controllMeasureExchangeSurname,
}: GeneralDataBornProps): React.ReactElement => {
  useEffect(() => {
    controllIsPaternitySentence(values?.isPaternitySentence);
  }, [values?.isPaternitySentence]);

  useEffect(() => {
    controllParentRecognitionMadrePadreRiconoscimento(values?.parentRecognition);
  }, [values?.parentRecognition]);

  useEffect(() => {
    controllParentChangedSurname(values?.parentChangedSurname);
  }, [values?.parentChangedSurname]);

  useEffect(() => {
    controllMeasureExchangeSurname(values?.measureExchangeSurname);
  }, [values?.measureExchangeSurname]);

  const handleListChangeSurname = useEventCallback((item?: ItemVM) => {
    onChange('changeSurname', item?.id);
  });

  const handleListPresentationDocument = useEventCallback((item?: ItemVM) => {
    onChange('presentationDocument', item?.id);
  });

  const handleListParentRecognition = useEventCallback((event: SelectChangeEvent<string>) => {
    onChanges({
      parentRecognition: event.target.value,
      sentenceRecognition: undefined, // campo non utilizzato in questa configurazione, ma salva sullo stesso campo
    });
  });

  const handleListTypeMutation = useEventCallback((item?: ItemVM) => {
    onChange('typeMutation', item?.id);
  });

  const handleListParentChangedSurname = useEventCallback((item?: ItemVM) => {
    onChange('parentChangedSurname', item?.id);
  });

  const handleListMeasureExchangeSurname = useEventCallback((item?: ItemVM) => {
    onChange('measureExchangeSurname', item?.id);
  });

  const isDsNas01: boolean = useMemo(() => {
    return configuration === ConfigurationType.DSNAS01;
  }, [configuration]);

  const isDsNas02: boolean = useMemo(() => {
    return configuration === ConfigurationType.DSNAS02;
  }, [configuration]);

  const isDsNas03: boolean = useMemo(() => {
    return configuration === ConfigurationType.DSNAS03;
  }, [configuration]);

  const isDsNas04: boolean = useMemo(() => {
    return configuration === ConfigurationType.DSNAS04;
  }, [configuration]);

  const isDsNas05: boolean = useMemo(() => {
    return configuration === ConfigurationType.DSNAS05;
  }, [configuration]);

  return (
    <Grid container spacing={2} item>
      <Grid item xs={12} md={6}>
        <ListAnsc
          label={isDsNas03 ? 'Scelta cambio cognome figlio' : 'Scelta cambio cognome'}
          idDominio="39"
          value={values?.changeSurname || ''}
          onChange={handleListChangeSurname}
          disabled={disabled}
          error={Boolean(errors?.changeSurname) && touched?.changeSurname}
          required
          betweenFrom={isDsNas02 || isDsNas03 || isDsNas04 || isDsNas05 ? 1 : 11}
          betweenTo={isDsNas02 || isDsNas03 || isDsNas04 || isDsNas05 ? 3 : 12}
        />
      </Grid>
      {isDsNas03 || isDsNas04 ? (
        <Grid item xs={12} md={6}>
          <PmiTextField
            label="Vecchio cognome genitore *"
            aria-label="Vecchio cognome cognome"
            id="oldSurname"
            name="oldSurname"
            fullWidth
            type="text"
            variant="outlined"
            value={values?.oldSurname || ''}
            onChange={(event) => {
              onChange('oldSurname', event.target.value);
            }}
            error={touched?.oldSurname && Boolean(errors?.oldSurname)}
            helperText={errors?.oldSurname}
            disabled={disabled}
          />
        </Grid>
      ) : undefined}
      <Grid item xs={12} md={6}>
        <PmiTextField
          label="Nuovo cognome *"
          aria-label="Nuovo cognome"
          id="newSurname"
          name="newSurname"
          fullWidth
          type="text"
          variant="outlined"
          value={values?.newSurname || ''}
          onChange={(event) => {
            onChange('newSurname', event.target.value);
          }}
          error={touched?.newSurname && Boolean(errors?.newSurname)}
          helperText={errors?.newSurname}
          disabled={disabled}
        />
      </Grid>
      {isDsNas02 || isDsNas03 || isDsNas04 || isDsNas05 ? (
        <Grid item xs={12} md={6}>
          <ListAnsc
            label="Dati prodotti"
            idDominio="36"
            value={values?.presentationDocument || ''}
            onChange={handleListPresentationDocument}
            disabled={disabled}
            error={Boolean(errors?.presentationDocument) && touched?.presentationDocument}
            required
          />
        </Grid>
      ) : undefined}
      {isDsNas03 ? (
        <Grid item xs={12} md={6}>
          <DatePicker
            label="Data provvedimento cambio cognome *"
            aria-label="Data provvedimento cambio cognome"
            value={values?.dateChangeSurname || null}
            onChange={(date) => {
              onChange('dateChangeSurname', date);
            }}
            disabled={disabled}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="dateChangeSurname"
                fullWidth
                helperText={errors?.dateChangeSurname}
                error={touched?.dateChangeSurname && Boolean(errors?.dateChangeSurname)}
              />
            )}
          />
        </Grid>
      ) : undefined}
      {isDsNas03 || isDsNas04 || isDsNas05 ? (
        <Grid item xs={12} md={6}>
          <DatePicker
            label={isDsNas03 || isDsNas05 ? 'Data dichiarazione *' : 'Data comunicazione scritta *'}
            aria-label={
              isDsNas03 || isDsNas05 ? 'Data dichiarazione' : 'Data comunicazione scritta'
            }
            value={values?.dateTranscription || null}
            onChange={(date) => {
              onChange('dateTranscription', date);
            }}
            disabled={disabled}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="dateTranscription"
                fullWidth
                helperText={errors?.dateTranscription}
                error={touched?.dateTranscription && Boolean(errors?.dateTranscription)}
              />
            )}
          />
        </Grid>
      ) : undefined}
      {isDsNas03 || isDsNas04 ? (
        <Fragment>
          <Grid item xs={12} md={6}>
            <ListAnsc
              label="Tipo mutazione"
              idDominio="76"
              value={values?.typeMutation || ''}
              onChange={handleListTypeMutation}
              disabled={disabled}
              error={Boolean(errors?.typeMutation) && touched?.typeMutation}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ListAnsc
              label="Genitore che ha mutato il cognome"
              idDominio="40"
              value={values?.parentChangedSurname || ''}
              onChange={handleListParentChangedSurname}
              disabled={disabled}
              error={Boolean(errors?.parentChangedSurname) && touched?.parentChangedSurname}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ListAnsc
              label="Provvedimento di cambio cognome"
              idDominio="85"
              value={values?.measureExchangeSurname || ''}
              onChange={handleListMeasureExchangeSurname}
              disabled={disabled}
              error={Boolean(errors?.measureExchangeSurname) && touched?.measureExchangeSurname}
              required
            />
          </Grid>
        </Fragment>
      ) : undefined}
      {isDsNas02 || isDsNas05 ? (
        <Grid item xs={12} md={6}>
          <ListParentRecognition
            value={values?.parentRecognition || ''}
            onChange={handleListParentRecognition}
            disabled={disabled}
            error={Boolean(errors?.parentRecognition) && touched?.parentRecognition}
            required
            label="Riconoscimento genitore"
          />
        </Grid>
      ) : undefined}
      {isDsNas01 ? (
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Checkbox
                id="isPaternitySentence"
                checked={values?.isPaternitySentence || false}
                onChange={() => {
                  onChange('isPaternitySentence', !values?.isPaternitySentence);
                }}
                inputProps={{ 'aria-label': 'Accertamento paternità con sentenza' }}
              />
            }
            label="Accertamento paternità con sentenza"
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

GeneralDataBornComponent.displayName = 'Form General Data component';

const GeneralDataBorn = memo(GeneralDataBornComponent);

export default GeneralDataBorn;
